<template>
  <div
    v-if="variant === 'page'"
    class="tw-min-h-[calc(100vh-60px)] tw-bg-gradient-to-br tw-from-slate-50 tw-via-white tw-to-indigo-50 tw-w-full"
  >
    <div class="tw-w-full tw-p-[10px]">
      <div class="tw-relative">
        <div
          v-if="loading"
          class="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-white/60 tw-backdrop-blur"
        >
          <div class="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/80 tw-px-4 tw-py-3 tw-shadow-sm">
            <font-awesome-icon icon="circle-notch" spin class="tw-h-4 tw-w-4 tw-text-slate-600" />
            <span class="tw-text-sm tw-font-medium tw-text-slate-700">{{ loadingText }}</span>
          </div>
        </div>
        <slot :selection="selectionApi" :pageSize="props.pageSize" :pageSizeOptions="normalizedPageSizeOptions" :setPageSize="setPageSize" />
      </div>
    </div>
  </div>

  <div
    v-else
    class="tw-relative tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-200/50"
    :class="cardPadding ? 'tw-p-3 sm:tw-p-4' : ''"
  >
    <div
      v-if="loading"
      class="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-white/60 tw-backdrop-blur"
    >
      <div class="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/80 tw-px-4 tw-py-3 tw-shadow-sm">
        <font-awesome-icon icon="circle-notch" spin class="tw-h-4 tw-w-4 tw-text-slate-600" />
        <span class="tw-text-sm tw-font-medium tw-text-slate-700">{{ loadingText }}</span>
      </div>
    </div>
    <slot :selection="selectionApi" :pageSize="props.pageSize" :pageSizeOptions="normalizedPageSizeOptions" :setPageSize="setPageSize" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

type ListPageVariant = 'page' | 'card';

const emit = defineEmits<{
  (e: 'update:pageSize', value: number): void;
}>();

const props = withDefaults(
  defineProps<{
    variant?: ListPageVariant | string;
    loading?: boolean;
    loadingText?: string;
    cardPadding?: boolean;
    enableSelection?: boolean;
    selectionItems?: any[];
    selectionKey?: string;
    pageSize?: number;
    pageSizeOptions?: number[];
  }>(),
  {
    variant: 'page',
    loading: false,
    loadingText: 'Loading…',
    cardPadding: true,
    enableSelection: false,
    selectionItems: () => [],
    selectionKey: 'id',
    pageSizeOptions: () => [10, 25, 50, 100, 250],
  },
);

const normalizedPageSizeOptions = computed<number[]>(() => {
  const opts = (props.pageSizeOptions || []).filter((x) => Number.isFinite(x) && x > 0);
  if (!opts.length) return [10, 25, 50, 100, 250];
  return Array.from(new Set(opts)).sort((a, b) => a - b);
});

function setPageSize(value: number) {
  if (!Number.isFinite(value)) return;
  emit('update:pageSize', value);
}

const selectedKeys = ref<Array<string | number>>([]);

const itemKeys = computed<Array<string | number>>(() => {
  if (!props.enableSelection) return [];
  const key = props.selectionKey;
  return (props.selectionItems || [])
    .map((item: any) => item?.[key])
    .filter((k: any) => k !== undefined && k !== null);
});

watch(
  itemKeys,
  (keys) => {
    if (!props.enableSelection) {
      selectedKeys.value = [];
      return;
    }

    const keySet = new Set(keys);
    selectedKeys.value = selectedKeys.value.filter((k) => keySet.has(k));
  },
  { immediate: true },
);

function isSelected(key: string | number): boolean {
  return selectedKeys.value.includes(key);
}

function toggleKey(key: string | number, toState: null | boolean = null) {
  if (!props.enableSelection) return;
  let isNextKeySelected = toState;
  if (isNextKeySelected === null) {
    isNextKeySelected = !isSelected(key);
  }
  if (!isNextKeySelected) {
    selectedKeys.value = selectedKeys.value.filter((k) => k !== key);
  } else {
    selectedKeys.value = [...selectedKeys.value, key];
  }
}

const allSelected = computed<boolean>(() => {
  if (!props.enableSelection) return false;
  const keys = itemKeys.value;
  if (!keys.length) return false;
  return keys.every((k) => selectedKeys.value.includes(k));
});

const someSelected = computed<boolean>(() => {
  if (!props.enableSelection) return false;
  return selectedKeys.value.length > 0 && !allSelected.value;
});

function toggleAll() {
  if (!props.enableSelection) return;
  if (allSelected.value) {
    selectedKeys.value = [];
  } else {
    selectedKeys.value = [...itemKeys.value];
  }
}

function clearSelection() {
  selectedKeys.value = [];
}

const selectionApi = computed(() => {
  return {
    enabled: props.enableSelection,
    keys: selectedKeys.value,
    count: selectedKeys.value.length,
    allSelected: allSelected.value,
    someSelected: someSelected.value,
    toggleKey,
    toggleAll,
    isSelected,
    clearSelection,
  };
});

defineExpose({ selectionApi });
</script>

<style>
.list-row-hover {
  transition: background-color 120ms ease;
}

.list-row-hover:hover {
  background-color: rgba(248, 250, 252, 0.7);
}
</style>
